// Équipes d'intervention (D-043) : équipes municipales et de prestataire, membres (chef ou membre),
// missions en cours ; création d'un prestataire et d'une équipe par le responsable technique.
import { schemaCreationEquipe } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, Dialogue, EtatVide, LienBouton, PuceEtat, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useUtilisateurs } from '../../api/requetes';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { nomUtilisateur } from '../administration/PageUtilisateurs';
import { useServices } from '../demarches/contenu';
import { creerEquipe, creerPrestataire, INVALIDER_INTERVENTIONS, useEquipes, usePrestataires } from './api';
import type { Equipe } from './types';
import commun from '../../commun/commun.module.css';

function FormulairePrestataire({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const [v, setV] = useState({ nom: '', contact: '', contrat_reference: '' });
  const action = useAction({
    executer: () => creerPrestataire({ nom: v.nom.trim(), contact: v.contact || undefined, contrat_reference: v.contrat_reference || undefined }),
    succes: (p) => `Prestataire ${p.nom} enregistré (code ${p.code}).`,
    invalider: INVALIDER_INTERVENTIONS,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message;
  return (
    <Dialogue ouvert={ouvert} titre="Nouveau prestataire" description="Fiche simple du prestataire ; son contrat relève du lot des marchés publics." onFermer={onFermer} actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Enregistrer</Bouton></>}>
      <Champ libelle="Nom" value={v.nom} onChange={(e) => setV({ ...v, nom: e.target.value })} erreur={champ('nom')} required />
      <Champ libelle="Contact" value={v.contact} onChange={(e) => setV({ ...v, contact: e.target.value })} facultatif />
      <Champ libelle="Référence du contrat" value={v.contrat_reference} onChange={(e) => setV({ ...v, contrat_reference: e.target.value })} facultatif />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function FormulaireEquipe({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const services = useServices();
  const prestataires = usePrestataires(ouvert);
  const utilisateurs = useUtilisateurs(ouvert);
  const [v, setV] = useState({ nom: '', type: 'municipale', prestataire_id: '', service_id: '', responsable: '' });
  const [membres, setMembres] = useState<Record<string, 'chef' | 'membre'>>({});
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaCreationEquipe.safeParse({
        nom: v.nom,
        type: v.type,
        prestataire_id: v.prestataire_id || undefined,
        service_id: v.service_id || undefined,
        responsable_appartenance_id: v.responsable || undefined,
        membres: Object.entries(membres).map(([appartenance_id, role]) => ({ appartenance_id, role })),
      });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerEquipe(corps.data);
    },
    succes: (e) => `Équipe ${e.nom} créée (code ${e.code}).`,
    invalider: INVALIDER_INTERVENTIONS,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  const agents = (utilisateurs.data ?? []).filter((u) => u.statut_appartenance === 'active');
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Nouvelle équipe"
      description="Le code est généré à partir du nom. Les membres d'une équipe de prestataire sont des comptes de prestataire : ils ne voient que les missions de leur équipe."
      onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Créer l&apos;équipe</Bouton></>}
    >
      <Champ libelle="Nom" value={v.nom} onChange={(e) => setV({ ...v, nom: e.target.value })} erreur={champ('nom')} required />
      <Selection libelle="Type" options={[{ valeur: 'municipale', libelle: 'Municipale' }, { valeur: 'prestataire', libelle: 'Prestataire' }]} value={v.type} onChange={(e) => setV({ ...v, type: e.target.value })} />
      {v.type === 'prestataire' ? <Selection libelle="Prestataire" optionVide="Choisir" options={(prestataires.data ?? []).map((p) => ({ valeur: p.id, libelle: p.nom }))} value={v.prestataire_id} onChange={(e) => setV({ ...v, prestataire_id: e.target.value })} erreur={champ('prestataire_id')} required /> : null}
      {v.type === 'municipale' ? <Selection libelle="Service" optionVide="Aucun" options={(services.data ?? []).map((s) => ({ valeur: s.id, libelle: s.nom }))} value={v.service_id} onChange={(e) => setV({ ...v, service_id: e.target.value })} facultatif /> : null}
      <Selection libelle="Responsable" optionVide="Aucun" options={agents.map((u) => ({ valeur: u.appartenance_id, libelle: nomUtilisateur(u) }))} value={v.responsable} onChange={(e) => setV({ ...v, responsable: e.target.value })} facultatif />
      <fieldset>
        <legend>Membres (cocher ; le premier coché est le chef)</legend>
        {agents.length === 0 ? <p>Aucun agent listé (droit de consultation des utilisateurs requis).</p> : null}
        {agents.map((u) => (
          <CaseACocher
            key={u.appartenance_id}
            libelle={`${nomUtilisateur(u)}${membres[u.appartenance_id] === 'chef' ? ' (chef)' : ''}`}
            checked={Boolean(membres[u.appartenance_id])}
            onChange={(e) => {
              const suivant = { ...membres };
              if (e.target.checked) suivant[u.appartenance_id] = Object.values(suivant).includes('chef') ? 'membre' : 'chef';
              else delete suivant[u.appartenance_id];
              setMembres(suivant);
            }}
          />
        ))}
      </fieldset>
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageEquipes() {
  const { contexte } = useSession();
  const equipes = useEquipes();
  const [creation, setCreation] = useState<'equipe' | 'prestataire' | null>(null);
  const colonnes: ColonneTableau<Equipe>[] = [
    { id: 'nom', entete: 'Équipe', enTeteLigne: true, masquable: false, valeurTri: (e) => e.nom, cellule: (e) => e.nom },
    { id: 'code', entete: 'Code', cellule: (e) => <span className={commun.reference}>{e.code}</span>, valeurTri: (e) => e.code },
    { id: 'type', entete: 'Type', cellule: (e) => (e.type === 'prestataire' ? <PuceEtat ton="neutre" libelle={`Prestataire : ${e.prestataire?.nom ?? ''}`} /> : <PuceEtat ton="ok" libelle="Municipale" />), valeurTri: (e) => e.type },
    { id: 'service', entete: 'Service', cellule: (e) => e.service?.nom ?? 'aucun' },
    { id: 'responsable', entete: 'Responsable', cellule: (e) => e.responsable?.nom ?? 'aucun' },
    { id: 'membres', entete: 'Membres', cellule: (e) => (e.membres.length ? e.membres.map((m) => `${m.nom ?? '?'}${m.role === 'chef' ? ' (chef)' : ''}`).join(', ') : 'aucun') },
    { id: 'missions', entete: 'Missions en cours', numerique: true, cellule: (e) => e.missions_en_cours.toLocaleString('fr-FR'), valeurTri: (e) => e.missions_en_cours },
    { id: 'actif', entete: 'État', cellule: (e) => (e.actif ? 'Active' : 'Inactive') },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Interventions"
        titre="Équipes"
        description="Équipes municipales ou de prestataire qui reçoivent les missions. Un compte de prestataire est un agent de terrain rattaché à une équipe de prestataire : il ne voit que ses missions."
        actions={
          <>
            <LienBouton href="/interventions" icone={ArrowLeft}>
              Interventions
            </LienBouton>
            {peut(contexte, 'intervention', 'creer') ? (
              <>
                <Bouton variante="secondaire" onClick={() => setCreation('prestataire')}>
                  Nouveau prestataire
                </Bouton>
                <Bouton variante="principal" icone={Plus} onClick={() => setCreation('equipe')}>
                  Nouvelle équipe
                </Bouton>
              </>
            ) : null}
          </>
        }
      />
      <EtatRequete chargement={equipes.isPending} erreur={equipes.error} onReessayer={() => void equipes.refetch()} lot="L3-A" fonction="La liste des équipes">
        {(equipes.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune équipe" icone={Users}>
            <p>Créer une équipe pour affecter des missions.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Équipes" colonnes={colonnes} lignes={equipes.data ?? []} cleLigne={(e) => e.id} cleColonnes="equipes" />
        )}
      </EtatRequete>
      <FormulaireEquipe ouvert={creation === 'equipe'} onFermer={() => setCreation(null)} />
      <FormulairePrestataire ouvert={creation === 'prestataire'} onFermer={() => setCreation(null)} />
    </>
  );
}
